import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface PostureMonitorProps {
  isMonitoring: boolean;
  currentBehavior?: string;
  confidence?: number;
}

const PostureMonitor: React.FC<PostureMonitorProps> = ({ 
  isMonitoring, 
  currentBehavior = 'Unknown',
  confidence = 0 
}) => {
  const images = useSelector((state: RootState) => state.dashboard.images);
  const latestImageUrl = images.length > 0 ? images[images.length - 1]?.image : null;

  return (
    <div className="rounded-xl dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted p-6 border border-claude-border dark:border-claude-darkBorder">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
          Real-time Posture Monitor
        </h3>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isMonitoring ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          <span className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {isMonitoring ? 'Live' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Camera Feed / Placeholder */}
      <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video mb-4">
        {latestImageUrl ? (
          <img 
            src={latestImageUrl} 
            alt="Dog posture" 
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-sm">Camera feed will appear here</span>
            <span className="text-xs mt-1">Start monitoring to see live feed</span>
          </div>
        )}
        
        {/* Pose Skeleton Overlay - Placeholder */}
        {isMonitoring && latestImageUrl && (
          <div className="absolute inset-0 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 640 480">
              {/* Simple skeleton placeholder */}
              <circle cx="320" cy="120" r="40" fill="none" stroke="#00ff00" strokeWidth="3" opacity="0.8" />
              <line x1="320" y1="160" x2="320" y2="280" stroke="#00ff00" strokeWidth="3" opacity="0.8" />
              <line x1="320" y1="200" x2="200" y2="280" stroke="#00ff00" strokeWidth="3" opacity="0.8" />
              <line x1="320" y1="200" x2="440" y2="280" stroke="#00ff00" strokeWidth="3" opacity="0.8" />
              <line x1="320" y1="280" x2="240" y2="400" stroke="#00ff00" strokeWidth="3" opacity="0.8" />
              <line x1="320" y1="280" x2="400" y2="400" stroke="#00ff00" strokeWidth="3" opacity="0.8" />
            </svg>
          </div>
        )}
      </div>

      {/* Current Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Current Behavior</span>
            <p className="text-sm font-medium text-gray-900 dark:text-claude-darkText">
              {currentBehavior}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Confidence</span>
            <p className="text-sm font-medium text-gray-900 dark:text-claude-darkText">
              {(confidence * 100).toFixed(0)}%
            </p>
          </div>
        </div>
        {isMonitoring && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1 animate-pulse" />
            REC
          </div>
        )}
      </div>
    </div>
  );
};

export default PostureMonitor;
